import React, { useState, useEffect, useRef } from 'react';
import { ref, get, set, update } from 'firebase/database';
import { database } from '../../firebase/config';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { pdf } from '@react-pdf/renderer';
import { formattaData } from '../../utils/dateUtils';
import SignatureCanvas from 'react-signature-canvas';
import { uploadImmagine } from '../../utils/cloudinaryUtils';

// ─────────────────────────────────────────────
// STILI PDF (identici a CaricaGiornaliera)
// ─────────────────────────────────────────────
const pdfStyles = StyleSheet.create({
  page: { padding: 20, fontSize: 8, fontFamily: 'Helvetica' },
  intestazione: { flexDirection: 'row', alignItems: 'center', marginBottom: 8, paddingBottom: 6, borderBottom: '1 solid #ccc' },
  logoImg: { width: 60, height: 24, objectFit: 'contain' },
  recapiti: { marginLeft: 10, fontSize: 7, color: '#333' },
  recapitiNome: { fontSize: 8, fontWeight: 'bold', marginBottom: 1 },
  headerRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 },
  headerCell: { fontSize: 9 },
  headerCellBold: { fontSize: 9, fontWeight: 'bold' },
  title: { fontSize: 12, fontWeight: 'bold', textAlign: 'center', marginBottom: 8 },
  divider: { borderBottom: '1 solid #aaa', marginVertical: 4 },
  sectionTitle: { fontSize: 8, fontWeight: 'bold', backgroundColor: '#e0e0e0', padding: 2, marginBottom: 2 },
  row: { flexDirection: 'row', marginBottom: 2 },
  label: { fontWeight: 'bold', width: 100 },
  value: { flex: 1 },
  orarioRow: { flexDirection: 'row', gap: 12, marginBottom: 2 },
  orarioBlock: { flexDirection: 'row' },
  footer: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 8, paddingTop: 6, borderTop: '1 solid #aaa' },
  footerLabel: { fontWeight: 'bold', marginBottom: 1 },
  firmaImg: { width: 160, height: 60, border: '1 solid #999' },
  modificaNote: { fontSize: 7, color: '#e74c3c', marginTop: 6, fontStyle: 'italic' },
});

// ─────────────────────────────────────────────
// COMPONENTE PDF
// ─────────────────────────────────────────────
function GiornalieraPDF({ dati }) {
  const { nomeDipendente, data, pausaInizio, pausaFine, lavori, oreTotali, modificatoIl } = dati;
  // oraFine obbligatoria per stampare il lavoro
  const lavoriValidi = (lavori || []).filter(l => (l.cantiereLabel || l.lavoroEseguito || l.oraInizio) && l.oraFine);

  return (
    <Document>
      <Page size="A4" style={pdfStyles.page}>
        <View style={pdfStyles.intestazione}>
          <Image src="CRGlogo.png" style={pdfStyles.logoImg} />
          <View style={pdfStyles.recapiti}>
            <Text style={pdfStyles.recapitiNome}>C.R.G. Scavi e trasporti Sagl</Text>
            <Text>Via Livio 5, 6830 Chiasso TI</Text>
            <Text>Tel +41 78 751 32 51 - email: info@crgscavi.ch</Text>
          </View>
        </View>

        <Text style={pdfStyles.title}>RAPPORTO GIORNALIERO</Text>

        <View style={pdfStyles.headerRow}>
          <Text style={pdfStyles.headerCellBold}>{nomeDipendente}</Text>
          <Text style={pdfStyles.headerCell}>{formattaData(data)}</Text>
        </View>

        <View style={pdfStyles.divider} />

        {lavoriValidi.map((lavoro, i) => (
          <View key={i}>
            <Text style={pdfStyles.sectionTitle}>Lavoro {i + 1}</Text>
            <View style={pdfStyles.row}>
              <Text style={pdfStyles.label}>Mezzo:</Text>
              <Text style={pdfStyles.value}>{lavoro.veicoloLabel || '-'}</Text>
            </View>
            <View style={pdfStyles.row}>
              <Text style={pdfStyles.label}>Lavoro eseguito:</Text>
              <Text style={pdfStyles.value}>{lavoro.lavoroEseguito || '-'}</Text>
            </View>
            <View style={pdfStyles.row}>
              <Text style={pdfStyles.label}>Cliente:</Text>
              <Text style={pdfStyles.value}>{lavoro.clienteLabel || '-'}</Text>
            </View>
            <View style={pdfStyles.row}>
              <Text style={pdfStyles.label}>Cantiere:</Text>
              <Text style={pdfStyles.value}>{lavoro.cantiereLabel || '-'}</Text>
            </View>
            <View style={pdfStyles.orarioRow}>
              <View style={pdfStyles.orarioBlock}>
                <Text style={pdfStyles.label}>Ora inizio: </Text>
                <Text>{lavoro.oraInizio || '-'}</Text>
              </View>
              <View style={pdfStyles.orarioBlock}>
                <Text style={pdfStyles.label}>Ora fine: </Text>
                <Text>{lavoro.oraFine || '-'}</Text>
              </View>
            </View>
            <View style={pdfStyles.divider} />
          </View>
        ))}

        <View style={pdfStyles.footer}>
          <View>
            <Text style={pdfStyles.footerLabel}>Pausa pranzo</Text>
            <Text>Inizio: {pausaInizio || '-'}   Fine: {pausaFine || '-'}</Text>
          </View>
          <View>
            <Text style={pdfStyles.footerLabel}>Ore totali lavorate</Text>
            <Text style={{ fontSize: 14, fontWeight: 'bold' }}>{oreTotali}</Text>
          </View>
        </View>

        {/* Firma dipendente */}
        <View style={{ marginTop: 20, borderTop: '1 solid #aaa', paddingTop: 10 }}>
          <Text style={pdfStyles.footerLabel}>Firma dipendente</Text>
          <Text style={{ marginBottom: 6 }}>{nomeDipendente}</Text>
          {dati.firmaUrl
            ? <Image src={dati.firmaUrl} style={pdfStyles.firmaImg} />
            : <Text style={{ color: '#999' }}>Non firmato</Text>
          }
        </View>

        {/* Nota modifica — appare SOLO se la giornaliera è stata modificata */}
        {modificatoIl && (
          <Text style={pdfStyles.modificaNote}>
            Modificata il: {modificatoIl} — {nomeDipendente}
          </Text>
        )}
      </Page>
    </Document>
  );
}

// ─────────────────────────────────────────────
// HELPER ore totali
// LOGICA PAUSA: detrai 1 ora (12:00-13:00) SOLO SE almeno un lavoro
// copre un orario compreso tra le 12:01 e le 12:59
// ─────────────────────────────────────────────
function calcolaOreTotali(lavori) {
  let totMinuti = 0;

  const PAUSA_SOGLIA_INIZIO = 12 * 60 + 1;  // 721 min = 12:01
  const PAUSA_SOGLIA_FINE   = 12 * 60 + 59; // 779 min = 12:59
  const PAUSA_DURATA        = 60;

  let detraiPausa = false;

  lavori.forEach(l => {
    if (l.oraInizio && l.oraFine) {
      const [h1, m1] = l.oraInizio.split(':').map(Number);
      const [h2, m2] = l.oraFine.split(':').map(Number);
      const ini = h1 * 60 + m1;
      const fin = h2 * 60 + m2;
      const diff = fin - ini;
      if (diff > 0) totMinuti += diff;

      // Copre la fascia pausa se: ini < 12:59 AND fin > 12:01
      if (ini < PAUSA_SOGLIA_FINE && fin > PAUSA_SOGLIA_INIZIO) {
        detraiPausa = true;
      }
    }
  });

  if (detraiPausa) {
    totMinuti = Math.max(0, totMinuti - PAUSA_DURATA);
  }

  if (totMinuti <= 0) return '0h 0min';
  const ore = Math.floor(totMinuti / 60);
  const min = totMinuti % 60;
  return `${ore}h ${min > 0 ? min + 'min' : ''}`.trim();
}

const LAVORI_VUOTI = Array(5).fill(null).map(() => ({
  veicoloId: '', lavoroEseguito: '', oraInizio: '', oraFine: '', clienteId: '', cantiereId: '',
  veicoloLabel: '', clienteLabel: '', cantiereLabel: '',
}));

// ─────────────────────────────────────────────
// COMPONENTE PRINCIPALE
// ─────────────────────────────────────────────
function MieGiornaliereForm({ dipendente, onBack }) {
  const [giornaliere, setGiornaliere] = useState([]);
  const [cantieri, setCantieri] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [veicoli, setVeicoli] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroData, setFiltroData] = useState('');

  // Stato modal modifica
  const [modalAperto, setModalAperto] = useState(false);
  const [giornalieraInModifica, setGiornalieraInModifica] = useState(null);
  const [lavori, setLavori] = useState(LAVORI_VUOTI.map(l => ({ ...l })));
  const [pausaInizio, setPausaInizio] = useState('');
  const [pausaFine, setPausaFine] = useState('');
  const [showFirma, setShowFirma] = useState(false);
  const [firmaBase64, setFirmaBase64] = useState(null);
  const [saving, setSaving] = useState(false);
  const [generandoPdf, setGenerandoPdf] = useState(null);
  const sigRef = useRef(null);

  useEffect(() => { caricaDati(); }, [dipendente]);

  async function caricaDati() {
    if (!dipendente) return;
    setLoading(true);
    const [giorSnap, cantSnap, cliSnap, veiSnap] = await Promise.all([
      get(ref(database, 'giornaliera')),
      get(ref(database, 'cantieri')),
      get(ref(database, 'clienti')),
      get(ref(database, 'veicoli')),
    ]);
    if (cantSnap.exists()) setCantieri(Object.entries(cantSnap.val()).map(([k, v]) => ({ id: k, ...v })));
    if (cliSnap.exists()) setClienti(Object.entries(cliSnap.val()).map(([k, v]) => ({ id: k, ...v })));
    if (veiSnap.exists()) setVeicoli(Object.entries(veiSnap.val()).map(([k, v]) => ({ id: k, ...v })));
    if (giorSnap.exists()) {
      const tutte = Object.entries(giorSnap.val()).map(([k, v]) => ({ id: k, ...v }));
      setGiornaliere(
        tutte
          .filter(g => g.dipendenteId === dipendente.id && g.tipo === 'form')
          .sort((a, b) => b.createdAt - a.createdAt)
      );
    } else {
      setGiornaliere([]);
    }
    setLoading(false);
  }

  // Controlla se entro 24h dalla creazione
  function isModificabile(g) {
    return Date.now() - g.createdAt < 24 * 60 * 60 * 1000;
  }

  function getLabelVeicolo(v) {
    return v ? `${v.marca} ${v.modello} - ${v.targa}` : '';
  }

  function getCantieriFiltrati(clienteId) {
    return clienteId ? cantieri.filter(c => c.clienteId === clienteId) : cantieri;
  }

  function updateLavoro(index, field, value) {
    setLavori(prev => {
      const copia = [...prev];
      copia[index] = { ...copia[index], [field]: value };
      if (field === 'clienteId') copia[index].cantiereId = '';
      return copia;
    });
  }

  // Apri modal con i dati della giornaliera selezionata
  function apriModifica(g) {
    setGiornalieraInModifica(g);
    const lavoriCaricati = Array(5).fill(null).map((_, i) => {
      const l = g.lavori?.[i];
      return l ? { ...l } : { ...LAVORI_VUOTI[0] };
    });
    setLavori(lavoriCaricati);
    setPausaInizio(g.pausaInizio || '');
    setPausaFine(g.pausaFine || '');
    setFirmaBase64(null);
    setModalAperto(true);
  }

  function confermaFirma() {
    if (sigRef.current && !sigRef.current.isEmpty()) {
      setFirmaBase64(sigRef.current.toDataURL('image/png'));
      setShowFirma(false);
    }
  }

  function cancellaPad() { sigRef.current?.clear(); }

  async function salvaMod() {
    const lavoriCompilati = lavori.filter(l => l.cantiereId || l.lavoroEseguito || l.oraInizio);
    if (lavoriCompilati.length === 0) {
      alert('Compila almeno una sezione lavoro.');
      return;
    }
    try {
      setSaving(true);
      const nomeDipendente = dipendente ? `${dipendente.nome} ${dipendente.cognome}` : '';
      // Usa la funzione aggiornata con logica pausa condizionale
      const oreTotali = calcolaOreTotali(lavori);

      const lavoriConLabel = lavori.map(l => ({
        ...l,
        veicoloLabel: l.veicoloId ? getLabelVeicolo(veicoli.find(v => v.id === l.veicoloId)) : (l.veicoloLabel || ''),
        clienteLabel: l.clienteId ? (clienti.find(c => c.id === l.clienteId)?.ragioneSociale || '') : (l.clienteLabel || ''),
        cantiereLabel: l.cantiereId ? (cantieri.find(c => c.id === l.cantiereId)?.luogo || '') : (l.cantiereLabel || ''),
      }));

      const now = new Date();
      const modificatoIl = now.toLocaleDateString('it-IT') + ' ' + now.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });

      let firmaUrl = giornalieraInModifica.firmaUrl || null;
      let firmaPubblicId = giornalieraInModifica.firmaPubblicId || null;
      if (firmaBase64) {
        const res = await fetch(firmaBase64);
        const firmaBlob = await res.blob();
        const firmaFile = new File([firmaBlob], 'firma.png', { type: 'image/png' });
        const { imageUrl, publicId } = await uploadImmagine(firmaFile, 'firme');
        firmaUrl = imageUrl;
        firmaPubblicId = publicId;
      }

      const aggiornamento = {
        pausaInizio,
        pausaFine,
        oreTotali,
        lavori: lavoriConLabel,
        firmaUrl,
        firmaPubblicId,
        modificatoIl,
        modificatoAt: Date.now(),
      };

      await update(ref(database, `giornaliera/${giornalieraInModifica.id}`), aggiornamento);

      const datiPDF = {
        ...giornalieraInModifica,
        ...aggiornamento,
        nomeDipendente,
      };
      const blob = await pdf(<GiornalieraPDF dati={datiPDF} />).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `giornaliera-modificata-${giornalieraInModifica.data}-${nomeDipendente.replace(/ /g, '_')}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);

      alert('Giornaliera modificata e PDF scaricato!');
      setModalAperto(false);
      setGiornalieraInModifica(null);
      await caricaDati();
    } catch (err) {
      alert('Errore: ' + err.message);
    } finally {
      setSaving(false);
    }
  }

  async function apriPDF(g) {
    setGenerandoPdf(g.id);
    try {
      const blob = await pdf(<GiornalieraPDF dati={g} />).toBlob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      alert('Errore generazione PDF: ' + err.message);
    } finally {
      setGenerandoPdf(null);
    }
  }

  const giornaliereFiltrate = filtroData
    ? giornaliere.filter(g => g.data === filtroData)
    : giornaliere;

  return (
    <div className="dashboard-container">
      <div className="sidebar">
        <h2><img src="CRGlogo.png" alt="crg" style={{ width: 150 }} /></h2>
        <div className="user-info">
          <p>{dipendente ? `${dipendente.nome} ${dipendente.cognome}` : ''}</p>
        </div>
        <button className="btn-logout" onClick={onBack}>Torna indietro</button>
      </div>

      <div className="form-bolla">
        <button className="btn-back-mobile" onClick={onBack} style={{ marginTop: 8 }}>Home</button>

        {/* FILTRO DATA */}
        <div className="form-group" style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 50, flexWrap: 'wrap' }}>
          <label style={{ whiteSpace: 'nowrap' }}>Filtra per data</label>
          <input type="date" value={filtroData} onChange={e => setFiltroData(e.target.value)} style={{ flex: 1 }} />
          {filtroData && <button className="btn-secondary small" onClick={() => setFiltroData('')}>Rimuovi</button>}
        </div>

        {/* LISTA GIORNALIERE */}
        {loading ? (
          <p style={{ color: '#666', textAlign: 'center' }}>Caricamento...</p>
        ) : giornaliereFiltrate.length === 0 ? (
          <p style={{ color: '#666', textAlign: 'center' }}>
            {filtroData ? `Nessuna giornaliera per il ${formattaData(filtroData)}` : 'Nessuna giornaliera compilata'}
          </p>
        ) : (
          <div className="bolle-list">
            {giornaliereFiltrate.map(g => (
              <div key={g.id} className="bolla-item">
                <div className="bolla-info">
                  <span className="bolla-data">{formattaData(g.data)}</span>
                  <span className="bolla-cantiere">
                    {(g.lavori || []).find(l => l.cantiereLabel)?.cantiereLabel || '—'}
                  </span>
                  <span style={{ fontSize: 11, color: '#27ae60', fontWeight: 600 }}>
                    {g.oreTotali}
                  </span>
                  {g.firmaUrl && <span className="bolla-firmata">Firmata</span>}
                  {g.modificatoIl && (
                    <span style={{ fontSize: 11, color: '#e67e22', background: '#fef9e7', borderRadius: 4, padding: '1px 6px', marginLeft: 4 }}>
                      Modificata
                    </span>
                  )}
                </div>
                <div className="bolla-azioni">
                  <button
                    className="btn-primary small"
                    onClick={() => apriPDF(g)}
                    disabled={generandoPdf === g.id}
                  >
                    {generandoPdf === g.id ? '...' : '📄 PDF'}
                  </button>
                  {isModificabile(g) && (
                    <button
                      className="btn-secondary small"
                      onClick={() => apriModifica(g)}
                      style={{ marginLeft: 8 }}
                    >
                      ✏️ Modifica
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* MODAL MODIFICA */}
      {modalAperto && giornalieraInModifica && (
        <div className="modal-overlay" onClick={() => setModalAperto(false)}>
          <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Modifica Giornaliera — {formattaData(giornalieraInModifica.data)}</h2>
              <button onClick={() => setModalAperto(false)} style={{ background: '#95a5a6', border: 'none', color: 'white', padding: '8px 15px', borderRadius: 5, cursor: 'pointer', fontSize: 18 }}>✕</button>
            </div>

            <div style={{ padding: 24, overflowY: 'auto', maxHeight: '70vh' }}>

              {/* 5 SEZIONI LAVORO */}
              {lavori.map((lavoro, i) => (
                <div key={i} style={{ marginBottom: 16, padding: 12, border: '1px solid #ddd', borderRadius: 8, background: '#fafafa' }}>
                  <h3 style={{ marginBottom: 10, color: '#2c3e50', fontSize: 14 }}>Lavoro {i + 1}</h3>

                  <div className="form-group">
                    <label>Mezzo di lavoro</label>
                    <select value={lavoro.veicoloId} onChange={e => updateLavoro(i, 'veicoloId', e.target.value)}>
                      <option value="">— Nessun mezzo —</option>
                      {veicoli.map(v => (
                        <option key={v.id} value={v.id}>{v.marca} {v.modello} - {v.targa}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Lavoro eseguito</label>
                    <textarea rows={2} value={lavoro.lavoroEseguito} onChange={e => updateLavoro(i, 'lavoroEseguito', e.target.value)} />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div className="form-group">
                      <label>Ora inizio</label>
                      <input type="time" value={lavoro.oraInizio} onChange={e => updateLavoro(i, 'oraInizio', e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label>Ora fine</label>
                      <input type="time" value={lavoro.oraFine} onChange={e => updateLavoro(i, 'oraFine', e.target.value)} />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Cliente</label>
                    <select value={lavoro.clienteId} onChange={e => updateLavoro(i, 'clienteId', e.target.value)}>
                      <option value="">— Seleziona cliente —</option>
                      {clienti.map(c => <option key={c.id} value={c.id}>{c.ragioneSociale}</option>)}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Cantiere</label>
                    <select value={lavoro.cantiereId} onChange={e => updateLavoro(i, 'cantiereId', e.target.value)}>
                      <option value="">— Seleziona cantiere —</option>
                      {getCantieriFiltrati(lavoro.clienteId).map(c => <option key={c.id} value={c.id}>{c.luogo}</option>)}
                    </select>
                  </div>
                </div>
              ))}

              {/* PAUSA PRANZO */}
              <div style={{ marginBottom: 16, padding: 12, border: '1px solid #ddd', borderRadius: 8, background: '#fff9e6' }}>
                <h3 style={{ marginBottom: 10, fontSize: 14, color: '#2c3e50' }}>Pausa pranzo</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="form-group">
                    <label>Inizio pausa</label>
                    <input type="time" value={pausaInizio} onChange={e => setPausaInizio(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>Fine pausa</label>
                    <input type="time" value={pausaFine} onChange={e => setPausaFine(e.target.value)} />
                  </div>
                </div>
                <p style={{ marginTop: 8, fontSize: 12, color: '#888' }}>⏱ La pausa viene detratta solo se si lavora oltre le 12:00</p>
              </div>

              {/* ORE TOTALI */}
              <div style={{ marginBottom: 16, padding: 14, background: '#e3f2fd', borderRadius: 8, textAlign: 'right' }}>
                <span style={{ fontWeight: 700, fontSize: 15 }}>
                  Ore totali stimate: {calcolaOreTotali(lavori)}
                </span>
              </div>

              {/* FIRMA */}
              <div style={{ marginBottom: 16, padding: 12, border: '1px solid #ddd', borderRadius: 8, background: '#f9f9f9' }}>
                <div className="firma-row">
                  <div className="firma-col">
                    <label>Nome dipendente</label>
                    <p className="autista-name">{dipendente ? `${dipendente.nome} ${dipendente.cognome}` : ''}</p>
                  </div>
                  <div className="firma-col">
                    <label>Firma dipendente</label>
                    {firmaBase64 ? (
                      <div>
                        <img src={firmaBase64} alt="Firma" className="firma-preview" />
                        <button className="btn-secondary small" onClick={() => setFirmaBase64(null)} style={{ marginTop: 4 }}>
                          Cancella firma
                        </button>
                      </div>
                    ) : giornalieraInModifica.firmaUrl ? (
                      <div>
                        <img src={giornalieraInModifica.firmaUrl} alt="Firma attuale" className="firma-preview" />
                        <button className="btn-secondary small" onClick={() => setShowFirma(true)} style={{ marginTop: 4 }}>
                          Cambia firma
                        </button>
                      </div>
                    ) : (
                      <button className="btn-secondary" onClick={() => setShowFirma(true)}>
                        Aggiungi firma
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* BOTTONE SALVA */}
              <button className="btn-primary full-width" onClick={salvaMod} disabled={saving}>
                {saving ? 'Salvataggio...' : 'Salva modifiche e scarica PDF'}
              </button>

            </div>
          </div>
        </div>
      )}

      {/* MODAL FIRMA */}
      {showFirma && (
        <div className="firma-overlay">
          <div className="firma-modal">
            <h3>Firma del dipendente</h3>
            <p>Firma con il dito</p>
            <div className="firma-canvas-wrapper">
              <SignatureCanvas ref={sigRef} canvasProps={{ className: 'firma-canvas' }} backgroundColor="white" />
            </div>
            <div className="firma-modal-buttons">
              <button className="btn-secondary" onClick={cancellaPad}>Cancella</button>
              <button className="btn-primary" onClick={confermaFirma}>Conferma</button>
              <button className="btn-danger" onClick={() => setShowFirma(false)}>Annulla</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default MieGiornaliereForm;
